import { useEffect, useState, useCallback, useRef } from 'react'
import {
  FileText, RefreshCw, Download, AlertTriangle, ChevronDown,
} from 'lucide-react'
import styles from './ReportsPage.module.css'

const DR_API = import.meta.env.VITE_DR_API_BASE_URL ?? ''

function authHeaders(): HeadersInit {
  const token = localStorage.getItem('dr_token')
  return token ? { Authorization: `Bearer ${token}` } : {}
}

// ── Types ──────────────────────────────────────────────────────────────────────

interface ReportType {
  value: string
  label: string
}

interface Report {
  id: number
  report_type: string
  title: string
  requested_by: string
  file_format: 'XLSX' | 'PDF'
  status: 'PENDING' | 'GENERATING' | 'COMPLETED' | 'FAILED'
  file_size_bytes: number | null
  generated_at: string | null
  created_at: string
  error_message: string | null
}

// ── Helpers ────────────────────────────────────────────────────────────────────

function fmtDate(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}

function fmtFileSize(bytes: number | null): string {
  if (bytes == null || bytes === 0) return '—'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`
}

function StatusBadge({ status }: { status: Report['status'] }) {
  if (status === 'COMPLETED') return <span className={styles.badgeGreen}>Completed</span>
  if (status === 'FAILED') return <span className={styles.badgeRed}>Failed</span>
  return (
    <span className={styles.badgeAmber}>
      <RefreshCw size={11} className={styles.spin} />
      {status === 'GENERATING' ? 'Generating' : 'Pending'}
    </span>
  )
}

function FormatBadge({ format }: { format: Report['file_format'] }) {
  return format === 'XLSX'
    ? <span className={styles.badgeGreen}>{format}</span>
    : <span className={styles.badgeBlue}>{format}</span>
}

// ── Main page ──────────────────────────────────────────────────────────────────

export function ReportsPage() {
  const [reportTypes, setReportTypes] = useState<ReportType[]>([])
  const [reports, setReports] = useState<Report[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Generate-report form state
  const [selectedType, setSelectedType] = useState('')
  const [selectedFormat, setSelectedFormat] = useState<'XLSX' | 'PDF'>('XLSX')
  const [generating, setGenerating] = useState(false)
  const [generateError, setGenerateError] = useState<string | null>(null)

  // Downloading state: set of report IDs currently downloading
  const [downloading, setDownloading] = useState<Set<number>>(new Set())

  // Auto-refresh interval ref
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const fetchReports = useCallback(async () => {
    try {
      const res = await fetch(`${DR_API}/api/v1/dr/reports/`, { headers: authHeaders() })
      if (!res.ok) throw new Error('API error')
      const data = await res.json()
      setReports(data.results ?? data)
    } catch {
      // silently keep last data on background refresh
    }
  }, [])

  const fetchAll = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [tRes, rRes] = await Promise.all([
        fetch(`${DR_API}/api/v1/dr/reports/types/`, { headers: authHeaders() }),
        fetch(`${DR_API}/api/v1/dr/reports/`, { headers: authHeaders() }),
      ])
      if (!tRes.ok || !rRes.ok) throw new Error('API error')
      const [tData, rData] = await Promise.all([tRes.json(), rRes.json()])
      setReportTypes(tData)
      const list: Report[] = rData.results ?? rData
      setReports(list)
      if (tData.length > 0 && !selectedType) setSelectedType(tData[0].value)
    } catch {
      setError('Cannot reach DR API. Make sure the dr-api service is running.')
    } finally {
      setLoading(false)
    }
  }, [selectedType])

  // Initial load
  useEffect(() => { fetchAll() }, [fetchAll])

  // Auto-refresh when any report is pending/generating
  useEffect(() => {
    const hasActive = reports.some(r => r.status === 'PENDING' || r.status === 'GENERATING')
    if (hasActive) {
      intervalRef.current = setInterval(fetchReports, 5000)
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
  }, [reports, fetchReports])

  const handleGenerate = async () => {
    if (!selectedType) return
    setGenerating(true)
    setGenerateError(null)
    try {
      const res = await fetch(`${DR_API}/api/v1/dr/reports/`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ report_type: selectedType, file_format: selectedFormat, parameters: {} }),
      })
      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        throw new Error(body.detail ?? 'Failed to generate report')
      }
      await fetchReports()
    } catch (err) {
      setGenerateError(err instanceof Error ? err.message : 'Unknown error')
    } finally {
      setGenerating(false)
    }
  }

  const handleDownload = async (report: Report) => {
    if (downloading.has(report.id)) return
    setDownloading(prev => new Set(prev).add(report.id))
    try {
      const res = await fetch(`${DR_API}/api/v1/dr/reports/${report.id}/download/`, {
        headers: authHeaders(),
      })
      if (!res.ok) throw new Error('Download failed')
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      const ext = report.file_format === 'XLSX' ? 'xlsx' : 'pdf'
      a.download = `${report.title ?? report.report_type}.${ext}`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch {
      // surface error inline if needed
    } finally {
      setDownloading(prev => {
        const next = new Set(prev)
        next.delete(report.id)
        return next
      })
    }
  }

  const typeLabel = (value: string) =>
    reportTypes.find(t => t.value === value)?.label ?? value

  return (
    <div className={styles.page}>

      {/* ── Header ── */}
      <div className={styles.hero}>
        <div className={styles.heroLeft}>
          <div className={styles.heroBreadcrumb}>
            <FileText size={12} />
            <span>Disaster Recovery</span>
            <span className={styles.breadSep}>/</span>
            <span>Reports</span>
          </div>
          <h1 className={styles.heroTitle}>DR Reports</h1>
          <p className={styles.heroSub}>
            Generate executive summaries, compliance reports, and audit exports for your
            disaster recovery programme.
          </p>
        </div>
        <button className={styles.btnSecondary} onClick={fetchAll} disabled={loading}>
          <RefreshCw size={13} className={loading ? styles.spin : ''} /> Refresh
        </button>
      </div>

      {/* ── Error ── */}
      {error && (
        <div className={styles.errorBanner}>
          <AlertTriangle size={15} />
          <span>{error}</span>
          <button className={styles.retryBtn} onClick={fetchAll}>Retry</button>
        </div>
      )}

      {/* ── Generate Report card ── */}
      <div className={styles.generateCard}>
        <div className={styles.generateCardHeader}>
          <FileText size={16} />
          <h2>Generate Report</h2>
        </div>

        <div className={styles.generateForm}>
          {/* Report type dropdown */}
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Report Type</label>
            <div className={styles.selectWrap}>
              <select
                className={styles.select}
                value={selectedType}
                onChange={e => setSelectedType(e.target.value)}
                disabled={generating || reportTypes.length === 0}
              >
                {reportTypes.length === 0 && (
                  <option value="">Loading types…</option>
                )}
                {reportTypes.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
              <ChevronDown size={14} className={styles.selectIcon} />
            </div>
          </div>

          {/* Format toggle */}
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Format</label>
            <div className={styles.formatToggle}>
              <button
                className={`${styles.formatBtn} ${selectedFormat === 'XLSX' ? styles.formatBtnActive : ''}`}
                onClick={() => setSelectedFormat('XLSX')}
                disabled={generating}
              >
                XLSX
              </button>
              <button
                className={`${styles.formatBtn} ${selectedFormat === 'PDF' ? styles.formatBtnActiveBlue : ''}`}
                onClick={() => setSelectedFormat('PDF')}
                disabled={generating}
              >
                PDF
              </button>
            </div>
          </div>

          {/* Submit */}
          <button
            className={styles.btnPrimary}
            onClick={handleGenerate}
            disabled={generating || !selectedType}
          >
            {generating ? (
              <>
                <RefreshCw size={13} className={styles.spin} />
                Generating…
              </>
            ) : (
              <>
                <FileText size={13} />
                Generate Report
              </>
            )}
          </button>
        </div>

        {generateError && (
          <div className={styles.generateError}>
            <AlertTriangle size={13} />
            {generateError}
          </div>
        )}
      </div>

      {/* ── Report history ── */}
      <div className={styles.tableSection}>
        <div className={styles.tableHeader}>
          <div className={styles.tableTitle}>
            <FileText size={15} />
            <h2>Report History</h2>
            <span className={styles.countBadge}>{reports.length} reports</span>
          </div>
        </div>

        {loading && (
          <div className={styles.loadingRow}>
            <RefreshCw size={18} className={styles.spin} />
            <span>Loading reports…</span>
          </div>
        )}

        {!loading && !error && reports.length === 0 && (
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon}><FileText size={32} /></div>
            <h3>No reports yet</h3>
            <p>Use the Generate Report panel above to create your first DR report.</p>
          </div>
        )}

        {!loading && reports.length > 0 && (
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Title / Type</th>
                  <th>Format</th>
                  <th>Status</th>
                  <th>Generated</th>
                  <th>Size</th>
                  <th>Requested By</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {reports.map(r => (
                  <tr key={r.id}>
                    <td>
                      <div className={styles.reportTitle}>
                        {r.title || typeLabel(r.report_type)}
                      </div>
                      {r.title && (
                        <div className={styles.reportTypeSub}>{typeLabel(r.report_type)}</div>
                      )}
                    </td>
                    <td><FormatBadge format={r.file_format} /></td>
                    <td>
                      <StatusBadge status={r.status} />
                      {r.status === 'FAILED' && r.error_message && (
                        <div className={styles.errorNote}>{r.error_message}</div>
                      )}
                    </td>
                    <td className={styles.dateCell}>{fmtDate(r.generated_at ?? r.created_at)}</td>
                    <td className={styles.numCell}>{fmtFileSize(r.file_size_bytes)}</td>
                    <td className={styles.reqBy}>{r.requested_by || '—'}</td>
                    <td>
                      {r.status === 'COMPLETED' && (
                        <button
                          className={styles.downloadBtn}
                          onClick={() => handleDownload(r)}
                          disabled={downloading.has(r.id)}
                        >
                          {downloading.has(r.id)
                            ? <RefreshCw size={13} className={styles.spin} />
                            : <Download size={13} />}
                          Download
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
